import { observer } from "mobx-react"
import React, { useMemo } from "react"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import { useTranslation } from "react-i18next"
import { useFormStore } from "components/ProjectSpecificComponents/Contexts/FormStoreContext"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import FormField, { TFormFieldDirection } from "../FormField"
import Select from "components/ProjectSpecificComponents/Elements/Global/atoms/Select"
import createReactSelectOptions from "../ToolFieldProperties/createReactSelectOptions"
import {
	isReactSelectSingleOption,
	TReactSelectSelectedOptions,
} from "components/ProjectSpecificComponents/Interfaces/ExternalComponentsInterfaces"
import { TSortDirection } from "components/ProjectSpecificComponents/Elements/Tables/organisms/Table/Interfaces/Interfaces"

interface Props {}

export const RequestTableFormDefaultSortOrderSection = observer(({}: Props) => {
	const { t } = useTranslation()
	const formStore = useFormStore()
	const { isMobile } = useScreenType()

	const [requestTable, defaultSortData, sortOrderFieldOptions] = useMemo(() => {
		if (formStore.selectedFormsConfig == null) return []

		let { requestTable } = formStore.selectedFormsConfig

		let defaultSortData = requestTable.find((item) => {
			return item.isDefaultSortField
		})

		let sortOrderFieldOptions = createReactSelectOptions(
			requestTable,
			"mainFieldKey",
			"mainFieldLabel"
		)

		return [requestTable, defaultSortData, sortOrderFieldOptions]
	}, [formStore.selectedFormsConfig])

	// !This should be moved to some sort of constants file
	const sortOrderOptions = [
		{
			label: t(`rawText.ascending`),
			value: "ascending",
		},
		{
			label: t(`rawText.descending`),
			value: "descending",
		},
	]

	const formFieldDirection: TFormFieldDirection = isMobile ? "Horizontal" : "Vertical"

	/**
	 * Sets isDefaultSortField based on selected field.
	 * If mainFieldKey matches selected field then isDefaultSortField is set to true else to false
	 */
	const setIsDefaultSortField = (selectedOption: TReactSelectSelectedOptions) => {
		if (requestTable == null) return

		requestTable.map((item, i) => {
			formStore.mergeRequestTableFormData(i, {
				isDefaultSortField:
					isReactSelectSingleOption(selectedOption) && item.mainFieldKey === selectedOption.value
						? true
						: false,
			})
		})
	}

	/**
	 * Sets sortOrder based on selected value.
	 * Finds index of object with isDefaultSortField true and sets sortOrder of that object with selected value
	 */
	const setSortOrder = (selectedOption: TReactSelectSelectedOptions) => {
		if (requestTable == null) return

		let index: number = requestTable.findIndex((item) => item.isDefaultSortField)
		// If default sort field isnt selected then return
		if (index < 0) return

		// selectedOption.value will always be TSortDirection
		formStore.mergeRequestTableFormData(index, {
			defaultSortOrder: isReactSelectSingleOption(selectedOption)
				? (selectedOption.value as TSortDirection)
				: undefined,
		})
	}

	return (
		<HyperDiv
			direction="column"
			padding="1rem"
			style={{ backgroundColor: "var(--light-light-gray)" }}
		>
			<HyperDiv
				className="hide-overflown-text-nowrap font-weight-bold"
				style={{
					fontSize: isMobile ? "16px" : "25px",
					color: "var(--muddy-puddle-gray)",
				}}
			>
				{t(`forms.header.default-sort-order`)}
			</HyperDiv>
			<Whitespace x="1" />
			<HyperDiv direction={isMobile ? "column" : "row"}>
				<HyperDiv className="fill-cter" style={{ maxWidth: isMobile ? "initial" : 250 }}>
					<FormField
						label={t(`forms.field-label.sort-field`)}
						placeholderValue=""
						editable
						direction={formFieldDirection}
					>
						<Select
							valueKey={defaultSortData?.mainFieldKey}
							onChange={setIsDefaultSortField}
							options={sortOrderFieldOptions ?? []}
						/>
					</FormField>
				</HyperDiv>
				<Whitespace x="3" h />
				<HyperDiv className="fill-cter" style={{ maxWidth: isMobile ? "initial" : 250 }}>
					<FormField
						label={t(`forms.field-label.sort-order`)}
						placeholderValue=""
						editable
						direction={formFieldDirection}
					>
						<Select
							isDisabled={!defaultSortData?.mainFieldKey}
							valueKey={defaultSortData?.defaultSortOrder}
							onChange={setSortOrder}
							options={sortOrderOptions ?? []}
						/>
					</FormField>
				</HyperDiv>
			</HyperDiv>
		</HyperDiv>
	)
})

export default RequestTableFormDefaultSortOrderSection
